import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Wrench, Plus, Search, Loader2, UserPlus, Building2, Phone, Mail, Eye, EyeOff, Pencil, IdCard, ArrowUpDown, Trash2, AlertTriangle } from 'lucide-react';
import PrestadorProfileView from '../components/PrestadorProfileView';
import { Checkbox } from '../components/ui/checkbox';

const vehicleOptions = [
  { value: 'carro', label: 'Carro' },
  { value: 'moto', label: 'Moto' },
  { value: 'sem_veiculo', label: 'Sem veículo' },
];

const funcaoSegurancaTipos = [
  'Policial Militar (PM)',
  'Policial Civil (PC)',
  'Policial Federal (PF)',
  'Policial Rodoviário Federal (PRF)',
  'Policial Penal',
  'Guarda Civil Municipal (GCM)',
  'Bombeiro Militar',
  'Vigilante Patrimonial',
  'Vigilante de Transporte de Valores',
  'Vigilante de Escolta Armada',
  'Vigilante de Segurança Pessoal Privada (VSPP)',
];

const emptyForm = {
  name: '', email: '', phone: '', password: '', supplier_cnpj: '',
  sexo: '', data_nascimento: '', agente_armado: '', vehicle_type: '',
  funcao_seguranca: '', funcao_seguranca_tipo: '', funcao_seguranca_ativo: false, antenista: '',
  emergency_contact_1_name: '', emergency_contact_1_phone: '',
  emergency_contact_2_name: '', emergency_contact_2_phone: ''
};

const PrestadoresPage = () => {
  const { token, user } = useAuth();
  const canDelete = ['mestre', 'adm'].includes(user?.role);
  const [prestadores, setPrestadores] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name_asc');
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [editingPrestador, setEditingPrestador] = useState(null);
  const [viewingPrestador, setViewingPrestador] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [form, setForm] = useState(emptyForm);

  const fetchData = async () => {
    try {
      const [usersData, suppliersData] = await Promise.all([
        api.get('/users', token),
        api.get('/suppliers', token)
      ]);
      const prestList = (usersData.users || usersData || []).filter(u => u.role === 'prestador');
      setPrestadores(prestList);
      setSuppliers((suppliersData || []).filter(s => s.active));
    } catch (e) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const resetForm = () => { setForm(emptyForm); setEditingPrestador(null); };

  const buildEmergencyContacts = () => {
    const contacts = [];
    if (form.emergency_contact_1_name || form.emergency_contact_1_phone) {
      contacts.push({ name: form.emergency_contact_1_name, phone: form.emergency_contact_1_phone });
    }
    if (form.emergency_contact_2_name || form.emergency_contact_2_phone) {
      contacts.push({ name: form.emergency_contact_2_name, phone: form.emergency_contact_2_phone });
    }
    return contacts;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.supplier_cnpj) { toast.error('Selecione um fornecedor'); return; }

    setSaving(true);
    try {
      if (editingPrestador) {
        const updateData = {
          name: form.name, phone: form.phone,
          sexo: form.sexo, data_nascimento: form.data_nascimento || null,
          agente_armado: form.agente_armado === 'true',
          vehicle_type: form.vehicle_type,
          funcao_seguranca: form.funcao_seguranca === 'true',
          funcao_seguranca_tipo: form.funcao_seguranca === 'true' ? form.funcao_seguranca_tipo : null,
          funcao_seguranca_ativo: !!form.funcao_seguranca_ativo,
          antenista: form.antenista === 'true',
          emergency_contacts: buildEmergencyContacts()
        };
        if (form.password) updateData.password = form.password;
        await api.put(`/users/${editingPrestador.id}`, updateData, token);
        toast.success('Prestador atualizado com sucesso!');
      } else {
        if (!form.password || form.password.length < 6) { toast.error('Senha deve ter pelo menos 6 caracteres'); setSaving(false); return; }
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register-prestador`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: form.name,
            email: form.email,
            password: form.password,
            phone: form.phone,
            supplier_cnpj: form.supplier_cnpj,
            sexo: form.sexo,
            data_nascimento: form.data_nascimento || null,
            agente_armado: form.agente_armado === 'true',
            vehicle_type: form.vehicle_type,
            funcao_seguranca: form.funcao_seguranca === 'true',
            funcao_seguranca_tipo: form.funcao_seguranca === 'true' ? form.funcao_seguranca_tipo : null,
            funcao_seguranca_ativo: !!form.funcao_seguranca_ativo,
            antenista: form.antenista === 'true',
            emergency_contacts: buildEmergencyContacts()
          })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Erro ao cadastrar');
        toast.success(`Prestador "${form.name}" cadastrado com sucesso!`);
      }
      setShowForm(false);
      resetForm();
      await fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (p) => {
    setEditingPrestador(p);
    setForm({
      name: p.name || '', email: p.email || '', phone: p.phone || '', password: '', supplier_cnpj: p.supplier_cnpj || '',
      sexo: p.sexo || '', data_nascimento: p.data_nascimento || '',
      agente_armado: p.agente_armado === true ? 'true' : p.agente_armado === false ? 'false' : '',
      vehicle_type: p.vehicle_type || '',
      funcao_seguranca: p.funcao_seguranca === true ? 'true' : p.funcao_seguranca === false ? 'false' : '',
      funcao_seguranca_tipo: p.funcao_seguranca_tipo || '',
      funcao_seguranca_ativo: !!p.funcao_seguranca_ativo,
      antenista: p.antenista === true ? 'true' : p.antenista === false ? 'false' : '',
      emergency_contact_1_name: p.emergency_contacts?.[0]?.name || '',
      emergency_contact_1_phone: p.emergency_contacts?.[0]?.phone || '',
      emergency_contact_2_name: p.emergency_contacts?.[1]?.name || '',
      emergency_contact_2_phone: p.emergency_contacts?.[1]?.phone || ''
    });
    setShowForm(true);
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/users/${p.id}`, { active: !(p.active !== false) }, token);
      toast.success(p.active !== false ? 'Prestador inativado' : 'Prestador ativado');
      await fetchData();
    } catch (e) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/users/${deleteTarget.id}`, token);
      toast.success(`Prestador "${deleteTarget.name}" excluído com sucesso`);
      setDeleteTarget(null);
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao excluir prestador');
    } finally {
      setDeleting(false);
    }
  };

  const getSupplierName = (cnpj) => {
    const s = suppliers.find(s => s.cnpj === cnpj);
    return s ? s.name : cnpj;
  };

  const filtered = prestadores
    .filter(p =>
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'name_asc') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'name_desc') return (b.name || '').localeCompare(a.name || '');
      if (sortBy === 'created_desc') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'created_asc') return new Date(a.created_at) - new Date(b.created_at);
      if (sortBy === 'rating_desc') return (b.rating_avg || 0) - (a.rating_avg || 0);
      return 0;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="prestadores-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pl-14">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Public Sans' }}>
            Prestadores
          </h1>
          <p className="text-slate-400 mt-1">{prestadores.length} prestadores cadastrados</p>
        </div>
        <Button
          onClick={() => { if (showForm) resetForm(); setShowForm(!showForm); }}
          className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl h-11 px-6"
          data-testid="new-prestador-btn"
        >
          {showForm ? 'Cancelar' : <><UserPlus className="w-4 h-4 mr-2" /> Novo Prestador</>}
        </Button>
      </div>

      {showForm && (
        <Card className="border-0 shadow-lg" data-testid="prestador-form">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-500" />
              {editingPrestador ? `Editar Prestador: ${editingPrestador.name}` : 'Cadastrar Novo Prestador'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome completo *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                  placeholder="Nome do prestador" data-testid="prestador-form-name" />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required
                  disabled={!!editingPrestador}
                  placeholder="email@exemplo.com" data-testid="prestador-form-email" />
              </div>
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                  placeholder="(11) 99999-9999" data-testid="prestador-form-phone" />
              </div>
              <div className="space-y-2">
                <Label>{editingPrestador ? 'Nova Senha (opcional)' : 'Senha *'}</Label>
                <div className="relative">
                  <Input type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                    required={!editingPrestador} minLength={6} placeholder="Mínimo 6 caracteres" data-testid="prestador-form-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Fornecedor vinculado *</Label>
                <Select value={form.supplier_cnpj} onValueChange={(v) => setForm({ ...form, supplier_cnpj: v })} disabled={!!editingPrestador}>
                  <SelectTrigger data-testid="prestador-form-supplier">
                    <SelectValue placeholder="Selecione o fornecedor" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.cnpj}>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">{s.name}</span>
                          <span className="text-xs text-slate-400">CNPJ: {s.cnpj}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2 pt-2 border-t">
                <p className="text-sm font-semibold text-slate-700 flex items-center gap-2"><IdCard className="w-4 h-4 text-slate-400" /> Dados Complementares</p>
              </div>

              <div className="space-y-2">
                <Label>Sexo</Label>
                <Select value={form.sexo} onValueChange={(v) => setForm({ ...form, sexo: v })}>
                  <SelectTrigger data-testid="prestador-form-sexo"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Masculino</SelectItem>
                    <SelectItem value="F">Feminino</SelectItem>
                    <SelectItem value="Outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Data de Nascimento</Label>
                <Input type="date" value={form.data_nascimento} onChange={e => setForm({ ...form, data_nascimento: e.target.value })}
                  data-testid="prestador-form-nascimento" />
              </div>
              <div className="space-y-2">
                <Label>Agente armado?</Label>
                <Select value={form.agente_armado} onValueChange={(v) => setForm({ ...form, agente_armado: v })}>
                  <SelectTrigger data-testid="prestador-form-armado"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Sim</SelectItem>
                    <SelectItem value="false">Não</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Veículo utilizado</Label>
                <Select value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v })}>
                  <SelectTrigger data-testid="prestador-form-vehicle"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {vehicleOptions.map(v => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Função de Segurança?</Label>
                <Select value={form.funcao_seguranca} onValueChange={(v) => setForm({ ...form, funcao_seguranca: v, funcao_seguranca_tipo: v === 'false' ? '' : form.funcao_seguranca_tipo })}>
                  <SelectTrigger data-testid="prestador-form-funcao-seguranca"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Sim</SelectItem>
                    <SelectItem value="false">Não</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.funcao_seguranca === 'true' && (
                <div className="space-y-2">
                  <Label>Tipo de Função de Segurança</Label>
                  <Select value={form.funcao_seguranca_tipo} onValueChange={(v) => setForm({ ...form, funcao_seguranca_tipo: v })}>
                    <SelectTrigger data-testid="prestador-form-funcao-seguranca-tipo"><SelectValue placeholder="Selecione o tipo" /></SelectTrigger>
                    <SelectContent>
                      {funcaoSegurancaTipos.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {form.funcao_seguranca === 'true' && (
                <div className="space-y-2 flex items-center gap-2 pt-6">
                  <Checkbox id="funcao-ativo" checked={form.funcao_seguranca_ativo} onCheckedChange={(v) => setForm({ ...form, funcao_seguranca_ativo: !!v })}
                    data-testid="prestador-form-funcao-ativo" />
                  <Label htmlFor="funcao-ativo" className="cursor-pointer">Função vigente/ativa atualmente</Label>
                </div>
              )}
              <div className="space-y-2">
                <Label>Antenista?</Label>
                <Select value={form.antenista} onValueChange={(v) => setForm({ ...form, antenista: v })}>
                  <SelectTrigger data-testid="prestador-form-antenista"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Sim</SelectItem>
                    <SelectItem value="false">Não</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2 space-y-2">
                <Label>Contato de Emergência 1</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Nome" value={form.emergency_contact_1_name} onChange={e => setForm({ ...form, emergency_contact_1_name: e.target.value })} data-testid="prestador-form-emergency1-name" />
                  <Input placeholder="Telefone" value={form.emergency_contact_1_phone} onChange={e => setForm({ ...form, emergency_contact_1_phone: e.target.value })} data-testid="prestador-form-emergency1-phone" />
                </div>
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label>Contato de Emergência 2 (opcional)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Nome" value={form.emergency_contact_2_name} onChange={e => setForm({ ...form, emergency_contact_2_name: e.target.value })} data-testid="prestador-form-emergency2-name" />
                  <Input placeholder="Telefone" value={form.emergency_contact_2_phone} onChange={e => setForm({ ...form, emergency_contact_2_phone: e.target.value })} data-testid="prestador-form-emergency2-phone" />
                </div>
              </div>

              <div className="md:col-span-2 flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); resetForm(); }}
                  className="rounded-xl">Cancelar</Button>
                <Button type="submit" disabled={saving}
                  className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl px-8" data-testid="prestador-form-submit">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingPrestador ? 'Salvar Alterações' : 'Cadastrar Prestador'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card className="border-0 bg-gradient-to-r from-amber-50 to-orange-50 shadow-sm">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Wrench className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-amber-900">URL de acesso dos prestadores</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Prestadores acessam pelo login principal com suas credenciais:
            </p>
            <code className="text-xs font-mono bg-white px-2 py-1 rounded mt-1 inline-block border border-amber-200 select-all" data-testid="prestador-url">
              {window.location.origin}/login
            </code>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Buscar prestadores..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="pl-9 rounded-xl" data-testid="search-prestadores" />
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-56 rounded-xl" data-testid="sort-prestadores">
            <ArrowUpDown className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name_asc">Nome (A-Z)</SelectItem>
            <SelectItem value="name_desc">Nome (Z-A)</SelectItem>
            <SelectItem value="created_desc">Cadastro (mais recente)</SelectItem>
            <SelectItem value="created_asc">Cadastro (mais antigo)</SelectItem>
            <SelectItem value="rating_desc">Nota (maior)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-0 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="prestadores-table">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Nome</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Email</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Telefone</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Fornecedor</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Status</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Cadastro</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-400">
                    <Wrench className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Nenhum prestador cadastrado</p>
                    <p className="text-xs mt-1">Clique em "Novo Prestador" para cadastrar</p>
                  </td>
                </tr>
              ) : (
                filtered.map(p => (
                  <tr key={p.id} className="border-b hover:bg-slate-50 transition-colors" data-testid={`prestador-row-${p.id}`}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                          <Wrench className="w-4 h-4 text-amber-600" />
                        </div>
                        <span className="font-medium text-sm text-slate-900">{p.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5 text-sm text-slate-600">
                        <Mail className="w-3.5 h-3.5 text-slate-400" />
                        {p.email}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5 text-sm text-slate-600">
                        <Phone className="w-3.5 h-3.5 text-slate-400" />
                        {p.phone || '-'}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-xs">
                        {p.supplier_name || getSupplierName(p.supplier_cnpj) || '-'}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Switch checked={p.active !== false} onCheckedChange={() => toggleActive(p)} data-testid={`toggle-active-prestador-${p.id}`} />
                        <Badge className={p.active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {p.active !== false ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-500">
                      {p.created_at ? new Date(p.created_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setViewingPrestador(p)}
                          className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
                          data-testid={`view-profile-prestador-${p.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(p)}
                          className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
                          data-testid={`edit-prestador-${p.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {canDelete && (
                          <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(p)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                            data-testid={`delete-prestador-${p.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!viewingPrestador} onOpenChange={(open) => !open && setViewingPrestador(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="prestador-profile-dialog">
          <DialogHeader>
            <DialogTitle>Perfil do Prestador — {viewingPrestador?.name}</DialogTitle>
          </DialogHeader>
          {viewingPrestador && <PrestadorProfileView prestadorId={viewingPrestador.id} mode="admin" />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="delete-prestador-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Excluir prestador
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Tem certeza que deseja excluir o prestador <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email})?
            Esta ação é permanente e não poderá ser desfeita.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}
              className="rounded-xl" data-testid="cancel-delete-prestador">
              Cancelar
            </Button>
            <Button onClick={handleDelete} disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white rounded-xl" data-testid="confirm-delete-prestador">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4 mr-2" /> Excluir</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrestadoresPage;
